import { BaseListViewStore, ShippingCostCellData, DateTimeCellData } from 'core/stores';
import { observable, action, makeObservable } from 'mobx';
import { localStorageHandler } from 'core/services';
import { BaasicTableSettingsViewStore } from 'common/stores';
import {
	TableFilterUtility,
	checkForColumnFilter,
	checkForColumnWidth,
	RemoveFiltersModalUtility,
	isSuperAdminOrganization,
} from 'core/utils';
import { TableFilterTemplate, TableFilterNumericTemplate, TableFilterDateTemplate } from 'themes/components';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, shipmentInfoTypeEnum } from 'common/constants';
import { CarrierServiceService, ApplicationService, OrderService, CarrierService } from 'common/data';
import { HyperLinkTrackingNumberCellData } from 'core/stores/CellDataStore';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import _ from 'lodash';

class ShipmentInfoReturnListViewStore extends BaseListViewStore {
	@observable change = false;
	@observable showReturnShipments = false;
	isSuperAdmin = false;
	isReturn = false;
	localStorageWidthKey = 'shipment-info-returns-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ShipmentInfoStore(rootStore),
			queryParams: {
				embed: [
					'order',
					'carrierService',
					'carrierService.carrierType',
					'carrier',
					'carrierDeliveryStatus',
					'carrierDeliveryStatus.shipmentInfoDeliveryStatus',
					'seller',
					'returnReason',
				],
				filter: localStorageHandler.get('shipment-info-returns-filter'),
			},
			tableData: {
				columns: [
					{
						key: 'trackingNumber',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGNUMBER',
						dataCell: HyperLinkTrackingNumberCellData,
						carrierTypePath: 'carrierService.carrierType.name',
						selected: true,
						width: 200,
						filter: null,
						locked: true,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						service: 'shipmentInfoService',
						filterApiParams: {
							shipmentInfoType: shipmentInfoTypeEnum.return,
							shippedOnly: true,
							ignoreEmptyTracking: true,
							excludeShipmentsWithNoCarrier: true,
						},
					},
					{
						key: 'seller.applicationTitle',
						title: 'SHIPMENTSTATUSVIEW.SELLER',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						locked: false,
						sortable: true,
						dontSplit: true,
						service: 'applicationService',
						enabled: isSuperAdminOrganization(rootStore),
						filterApiParams: {
							sellerOnly: true,
						},
					},
					{
						key: 'order.orderNumber',
						title: 'SHIPMENTSTATUSVIEW.ORDERNUMBER',
						service: 'orderService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						filterApiParams: {
							shipmentInfoType: shipmentInfoTypeEnum.return,
							checkPartiallyFulfillMixedOrders: true,
							sellerOnly: true,
							excludeShipmentsWithNoCarrier: true,
						},
					},
					{
						key: 'deliveryDate',
						title: 'SHIPMENTSTATUSVIEW.ESTIMATEDDELIVERYDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'l LT',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'cost',
						title: 'SHIPMENTSTATUSVIEW.COST',
						selected: false,
						dataCell: ShippingCostCellData,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'carrierService.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIERSERVICE',
						service: 'carrierServiceService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'carrier.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIER',
						service: 'carrierService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'carrierDeliveryStatus.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIERDELIVERYSTATUS',
						lookupConstant: lookupRouteName.carrierDeliveryStatus,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						embed: ['carrierDeliveryStatus'],
					},
					{
						key: 'carrierDeliveryStatus.shipmentInfoDeliveryStatus.name',
						title: 'SHIPMENTSTATUSVIEW.SHIPMENTINFODELIVERYSTATUS',
						lookupConstant: lookupRouteName.shipmentInfoDeliveryStatus,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						embed: ['carrierDeliveryStatus', 'carrierDeliveryStatus.shipmentInfoDeliveryStatus'],
					},
					{
						key: 'internalNote',
						title: 'SHIPMENTSTATUSVIEW.INTERNALNOTE',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'zone',
						title: 'SHIPMENTSTATUSVIEW.ZONE',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterNumericTemplate,
					},
					{
						key: 'returnReason.name',
						title: 'SHIPMENTSTATUSVIEW.RETURNREASON',
						lookupConstant: lookupRouteName.returnReason,
						selected: true,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
				],
				sortObject: [
					{
						field: 'trackingNumber',
						dir: 'asc',
					},
				],
				actions: {},
			},
			permissions: {
				update: () => this.authStore.hasPermission('fulfillmentSection.update'),
				create: () => this.authStore.hasPermission('fulfillmentSection.create'),
				remove: () => this.authStore.hasPermission('fulfillmentSection.remove'),
				read: () => this.authStore.hasPermission('fulfillmentSection.read'),
				full: () => this.authStore.hasPermission('fulfillmentSection.full'),
			},
			routes: {},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(rootStore)) {
			this.isSuperAdmin = true;
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findShipmentInfoReturns);
		checkForColumnFilter(localStorageHandler.get('shipment-info-returns-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.returnStore = new ShipmentInfoStore(rootStore);
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [
				this.listStore.service,
				new OrderService(rootStore.app.baasic.apiClient),
				new CarrierServiceService(rootStore.app.baasic.apiClient),
				new ApplicationService(rootStore.app.baasic.apiClient),
				new CarrierService(rootStore.app.baasic.apiClient),
			],
		});
		this.setSearchVisible();
		this.setButtons();
	}

	@action.bound
	setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
			],
		});
	}

	@action.bound
	findShipmentInfoReturns = async () => {
		this.suspend();
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'ShipmentInfoReturnTableSettings',
			this.setAsChanged,
			this.findShipmentInfoReturns
		);
		this.queryUtility.setFilterParams(
			this,
			{
				ignoreEmptyTracking: true,
				shipmentInfoType: shipmentInfoTypeEnum.return,
			},
			this.tableFilter
		);
		await this.listStore.find(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.queryUtility.setFilterPersistance(this, 'shipment-info-returns-filter', 'shipment-info-returns-table-data');
		this.setButtons();
		this.resume();
	};

	@action.bound
	setAsChanged() {
		this.change = !this.change;
	}
}

export default ShipmentInfoReturnListViewStore;



// WEBPACK FOOTER //
// ./src/modules/shipment-info-return/stores/ShipmentInfoReturnListViewStore.js